import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { CreditCard, Loader2, Search, Store } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { ChainPayModal } from '@/components/invoices/ChainPayModal';
import { cn } from '@/lib/utils';
import { formatUsd } from '@/lib/format';
import { fmtMirrorDate } from '@/lib/dates';
import { useGroupInvoices, type GroupInvoiceRow } from '@/hooks/useGroupInvoices';

/**
 * /pay for a chain's ACCOUNTING login — every store's payable invoices, not
 * just the master's own handful.
 *
 * ⚠ WHY THIS EXISTS. /invoices, the merged statement, equipment, service and
 * cylinders were all given a chain view; /pay was not, so it kept reading the
 * caller's own account through RLS. Standing on THE MELT MAIN it listed the
 * master's few invoices while Home, the statement and the invoice list all
 * quoted the whole chain — the same number disagreeing with itself on the one
 * screen whose entire job is "pay what you owe".
 *
 * The list here is deliberately NARROWER than /invoices: only what can
 * actually be charged right now. Paid rows, rows with a payment already
 * settling, and rentals autopay is about to charge are all out — a pay screen
 * that lists something it will then refuse is worse than one that doesn't
 * show it.
 */

const ALL_STORES = '__all__';

export default function ChainPay() {
  const { data, isLoading } = useGroupInvoices();

  const [store, setStore] = useState<string>(ALL_STORES);
  const [term, setTerm] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [payOpen, setPayOpen] = useState(false);

  // Payable = open and not already settling. Rentals on autopay ARE included.
  const payable = useMemo(
    () =>
      (data?.invoices ?? []).filter(
        // ⚠ rental_autopay is deliberately NOT excluded — see Pay.tsx. Enrolment
        //   is not a charge; an open invoice stays payable. payment_in_flight is
        //   the guard that matters, and it stays.
        (r) => r.is_open && r.balance > 0 && !r.payment_in_flight,
      ),
    [data],
  );

  const inFlightCount = useMemo(
    () => (data?.invoices ?? []).filter((r) => r.is_open && r.payment_in_flight).length,
    [data],
  );

  const rows = useMemo(() => {
    const q = term.trim().toLowerCase();
    return payable.filter((r) => {
      if (store !== ALL_STORES && r.customer_id !== store) return false;
      if (!q) return true;
      const hay = `${r.doc_number ?? ''} ${r.store_name} ${r.store_code ?? ''}`.toLowerCase();
      return hay.includes(q);
    });
  }, [payable, store, term]);

  // A selection must never outlive the rows it was made on — filtering to one
  // store with 40 ticked would otherwise leave "Pay 40" armed against
  // invoices that are no longer on screen.
  const visibleIds = useMemo(() => rows.map((r) => r.qbo_invoice_id), [rows]);
  useEffect(() => {
    setSelected((prev) => {
      if (prev.size === 0) return prev;
      const keep = new Set(visibleIds.filter((id) => prev.has(id)));
      return keep.size === prev.size ? prev : keep;
    });
  }, [visibleIds]);

  const selectedRows = rows.filter((r) => selected.has(r.qbo_invoice_id));
  const selectedTotal = selectedRows.reduce((s, r) => s + r.balance, 0);
  const allVisibleSelected = visibleIds.length > 0 && visibleIds.every((id) => selected.has(id));

  const toggleOne = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const brand = data?.master?.group_brand || data?.master?.name || 'your stores';
  const total = payable.reduce((s, r) => s + r.balance, 0);
  const storeCount = new Set(payable.map((r) => r.customer_id)).size;

  return (
    <div className="h-full overflow-y-auto scrollbar-thin pb-16 md:pb-10">
      <div className="px-6 md:px-8 pt-8 pb-12 max-w-5xl mx-auto">
        <h1 className="mb-1 text-center font-display text-2xl font-bold tracking-tight text-brix-ink">
          Pay — all stores
        </h1>
        <p className="text-center text-base mb-7 text-brix-muted max-w-2xl mx-auto">
          Everything payable across {brand}. Tick what you're paying — each store is charged on
          its own account, so every one gets its own QuickBooks payment and receipt.
        </p>

        <div className="grid grid-cols-2 gap-4 mb-6">
          <Tile label="Payable now" value={isLoading ? '—' : formatUsd(total)} />
          <Tile
            label="Invoices"
            value={isLoading ? '—' : String(payable.length)}
            sub={storeCount > 0 ? `across ${storeCount} store${storeCount === 1 ? '' : 's'}` : undefined}
          />
        </div>

        {inFlightCount > 0 && (
          <p className="mb-4 rounded-xl border border-brix-border bg-brix-surface-alt/40 px-4 py-3 text-sm text-brix-muted">
            {inFlightCount} invoice{inFlightCount === 1 ? '' : 's'} already{' '}
            {inFlightCount === 1 ? 'has' : 'have'} a payment settling, so{' '}
            {inFlightCount === 1 ? "it isn't" : "they aren't"} listed here. Card payments clear
            within a day; bank (ACH) payments take 3–5 business days, and the invoice stays locked
            until then so it isn't paid twice. See{' '}
            <Link to="/invoices" className="font-semibold text-brix-primary hover:underline">
              Invoices
            </Link>{' '}
            for the full picture.
          </p>
        )}

        <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-4">
          <div className="relative flex-1">
            <Search
              size={15}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-brix-muted pointer-events-none"
            />
            <input
              value={term}
              onChange={(e) => setTerm(e.target.value)}
              placeholder="Invoice # or store"
              className="w-full rounded-xl border border-brix-border bg-brix-surface pl-9 pr-3 py-2 text-sm"
            />
          </div>
          <div className="relative">
            <Store
              size={15}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-brix-muted pointer-events-none"
            />
            <select
              value={store}
              onChange={(e) => setStore(e.target.value)}
              className="rounded-xl border border-brix-border bg-brix-surface pl-9 pr-3 py-2 text-sm"
            >
              <option value={ALL_STORES}>All stores</option>
              {(data?.stores ?? []).map((s) => (
                <option key={s.customer_id} value={s.customer_id}>
                  {s.store_code ? `#${s.store_code} · ` : ''}
                  {s.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        {isLoading ? (
          <p className="py-10 text-center text-sm text-brix-muted">
            <Loader2 size={16} className="animate-spin inline-block mr-2" />
            Loading…
          </p>
        ) : rows.length === 0 ? (
          <p className="py-10 text-center text-sm text-brix-muted">Nothing to pay right now.</p>
        ) : (
          <div className="rounded-2xl border border-brix-border overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-brix-surface-alt/60 text-[11px] uppercase tracking-wide text-brix-muted">
                <tr>
                  <th className="px-3 py-2.5 w-10">
                    <input
                      type="checkbox"
                      aria-label="Select all"
                      checked={allVisibleSelected}
                      ref={(el) => {
                        if (el) el.indeterminate = selected.size > 0 && !allVisibleSelected;
                      }}
                      onChange={() =>
                        setSelected(allVisibleSelected ? new Set() : new Set(visibleIds))
                      }
                    />
                  </th>
                  <th className="px-3 py-2.5 text-left font-semibold">Invoice</th>
                  <th className="px-3 py-2.5 text-left font-semibold">Store</th>
                  <th className="px-3 py-2.5 text-left font-semibold hidden sm:table-cell">Due</th>
                  <th className="px-3 py-2.5 text-right font-semibold">Balance</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <Row
                    key={r.qbo_invoice_id}
                    row={r}
                    checked={selected.has(r.qbo_invoice_id)}
                    onToggle={() => toggleOne(r.qbo_invoice_id)}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}

        {selectedRows.length > 0 && (
          <div className="sticky bottom-4 mt-4 flex flex-wrap items-center gap-3 rounded-2xl border border-brix-border bg-brix-surface px-4 py-3 shadow-card-deep">
            <span className="text-sm text-brix-ink">
              <span className="font-bold">{selectedRows.length}</span> selected ·{' '}
              <span className="font-bold tabular-nums">{formatUsd(selectedTotal)}</span>
            </span>
            <button
              type="button"
              onClick={() => setSelected(new Set())}
              className="text-sm text-brix-muted hover:underline"
            >
              Clear
            </button>
            <Button className="ml-auto" onClick={() => setPayOpen(true)}>
              <CreditCard size={15} /> Pay {formatUsd(selectedTotal)}
            </Button>
          </div>
        )}
      </div>

      {payOpen && (
        <ChainPayModal
          invoiceIds={selectedRows.map((r) => r.qbo_invoice_id)}
          onClose={() => {
            setPayOpen(false);
            setSelected(new Set());
          }}
        />
      )}
    </div>
  );
}

function Row({
  row,
  checked,
  onToggle,
}: {
  row: GroupInvoiceRow;
  checked: boolean;
  onToggle: () => void;
}) {
  return (
    <tr
      onClick={onToggle}
      className={cn(
        'border-t border-brix-border/70 cursor-pointer transition-colors',
        checked ? 'bg-brix-primary-tint/40' : 'hover:bg-brix-primary-tint/20',
      )}
    >
      <td className="px-3 py-2.5" onClick={(e) => e.stopPropagation()}>
        <input type="checkbox" checked={checked} onChange={onToggle} aria-label="Select invoice" />
      </td>
      <td className="px-3 py-2.5 font-medium text-brix-ink">
        {row.doc_number ?? row.qbo_invoice_id}
      </td>
      <td className="px-3 py-2.5 text-brix-muted">
        {row.store_code ? `#${row.store_code} · ` : ''}
        {row.store_name}
      </td>
      <td className="px-3 py-2.5 hidden sm:table-cell">
        {row.due_date ? fmtMirrorDate(row.due_date) : '—'}
        {row.days_past_due > 0 && (
          <span className="ml-2 text-[11px] font-semibold text-brix-warning">
            {row.days_past_due}d late
          </span>
        )}
      </td>
      <td className="px-3 py-2.5 text-right tabular-nums font-semibold">
        {formatUsd(row.balance)}
      </td>
    </tr>
  );
}

function Tile({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div className="rounded-2xl border border-brix-border bg-brix-surface px-4 py-3">
      <p className="text-[11px] uppercase tracking-wide text-brix-muted font-semibold">{label}</p>
      <p className="font-display text-xl font-bold text-brix-ink tabular-nums">{value}</p>
      {sub && <p className="text-xs text-brix-muted mt-0.5">{sub}</p>}
    </div>
  );
}
